import {startTransition, useEffect, useState} from 'react'

import {IssueViewerLoading} from './IssueViewerLoading'
import type {OptionConfig} from './OptionConfig'
import {NotFoundError} from './shared/IssueViewerError'

const OPTIMISTIC_RETRY_DELAY_MS = 1500
const OPTIMISTIC_MAX_RETRIES = 5

export {OPTIMISTIC_MAX_RETRIES, OPTIMISTIC_RETRY_DELAY_MS}

/**
 * Shows a loading skeleton and auto-retries the query when a sub-issue from an optimistic
 * update isn't available on the backend yet. Falls back to NotFoundError after max retries.
 *
 * Uses startTransition so that calling retryQuery (which triggers loadIssueViewerView →
 * usePreloadedQuery suspension) does not cause Suspense to unmount this component and
 * reset the retry counter.
 */
export function OptimisticUpdateRetry({
  retryQuery,
  optionConfig,
}: {
  retryQuery: () => void
  optionConfig: Pick<OptionConfig, 'useViewportQueries'>
}) {
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    if (retryCount >= OPTIMISTIC_MAX_RETRIES) return
    const timeoutId = setTimeout(() => {
      startTransition(() => {
        retryQuery()
      })
      setRetryCount(c => c + 1)
    }, OPTIMISTIC_RETRY_DELAY_MS)
    return () => clearTimeout(timeoutId)
  }, [retryQuery, retryCount])

  if (retryCount >= OPTIMISTIC_MAX_RETRIES) {
    return <NotFoundError />
  }

  return <IssueViewerLoading optionConfig={optionConfig} />
}
